import React, { useState, useEffect, useCallback } from 'react';
import { 
  Mail, Send, TrendingUp, DollarSign, Users, MousePointer,
  Eye, ShoppingCart, RefreshCw, Sparkles, Clock, CheckCircle,
  XCircle, AlertTriangle, ChevronRight, Plus, Settings,
  BarChart3, Zap, Target, Gift, UserCheck, FileText
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export default function CampaignDashboard() {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [triggers, setTriggers] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [gdprSettings, setGdprSettings] = useState(null);
  
  // Campaign Creation
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creatingCampaign, setCreatingCampaign] = useState(false);
  const [newCampaign, setNewCampaign] = useState(null);
  const [selectedTriggerType, setSelectedTriggerType] = useState('reactivation');
  const [selectedSegment, setSelectedSegment] = useState('abwanderungsgefaehrdet');
  const [discountPercent, setDiscountPercent] = useState(10);
  
  // Campaign Preview
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('access_token') || localStorage.getItem('access_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  };

  const fetchDashboard = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/campaigns/dashboard`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setDashboard(data);
      }
    } catch (error) {
      console.error('Dashboard fetch error:', error);
    }
  }, []);

  const fetchTriggers = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/campaigns/triggers`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setTriggers(data.triggers || []);
      }
    } catch (error) {
      console.error('Triggers fetch error:', error);
    }
  }, []);

  const fetchCampaigns = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/campaigns/campaigns`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setCampaigns(data.campaigns || []);
      }
    } catch (error) {
      console.error('Campaigns fetch error:', error);
    }
  }, []);

  const fetchGdprSettings = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/campaigns/settings/gdpr`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setGdprSettings(data);
      }
    } catch (error) {
      console.error('GDPR settings fetch error:', error);
    }
  }, []);

  const seedDemoData = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/campaigns/demo/seed`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        toast.success('Demo-Daten erstellt');
        await Promise.all([fetchDashboard(), fetchCampaigns()]);
      }
    } catch (error) {
      toast.error('Fehler beim Erstellen der Demo-Daten');
    }
  };

  const generateCampaign = async () => {
    setCreatingCampaign(true);
    try {
      const params = new URLSearchParams({
        trigger_type: selectedTriggerType,
        segment_key: selectedSegment,
        discount_percent: discountPercent.toString()
      });
      
      const response = await fetch(`${API_BASE}/api/campaigns/generate?${params}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        setNewCampaign(data);
        setShowPreviewModal(true);
        setShowCreateModal(false);
        toast.success('KI-Kampagne generiert!');
        await fetchCampaigns();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler bei der Generierung');
      }
    } catch (error) {
      toast.error('Netzwerkfehler');
    } finally {
      setCreatingCampaign(false);
    }
  };

  const approveCampaign = async (campaignId) => {
    try {
      const response = await fetch(`${API_BASE}/api/campaigns/campaigns/${campaignId}/approve`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success('Kampagne freigegeben!', {
          description: data.message
        });
        setShowPreviewModal(false);
        await Promise.all([fetchDashboard(), fetchCampaigns()]);
      }
    } catch (error) {
      toast.error('Fehler beim Freigeben');
    }
  };

  const rejectCampaign = async (campaignId) => {
    try {
      const response = await fetch(`${API_BASE}/api/campaigns/campaigns/${campaignId}/reject`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        toast.success('Kampagne abgelehnt');
        setShowPreviewModal(false);
        await fetchCampaigns();
      }
    } catch (error) {
      toast.error('Fehler beim Ablehnen');
    }
  };

  const updateTrigger = async (triggerId, updates) => {
    try {
      const params = new URLSearchParams(updates);
      const response = await fetch(`${API_BASE}/api/campaigns/triggers/${triggerId}?${params}`, {
        method: 'PUT',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        toast.success('Trigger aktualisiert');
        await fetchTriggers();
      }
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([fetchDashboard(), fetchTriggers(), fetchCampaigns(), fetchGdprSettings()]);
      setLoading(false);
    };
    loadAll();
  }, [fetchDashboard, fetchTriggers, fetchCampaigns, fetchGdprSettings]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('de-DE', { 
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' 
    });
  };

  const getTriggerIcon = (type) => {
    const icons = {
      reactivation: Clock,
      welcome: UserCheck,
      vip_thanks: Gift,
      favorite_dish: Target
    };
    const Icon = icons[type] || Mail;
    return <Icon className="w-5 h-5" />;
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen" data-testid="campaign-loading">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-500" />
          <p className="text-gray-400">Lade KI-Kampagnen...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="campaign-dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Sparkles className="w-8 h-8 text-purple-400" />
            KI-Umsatz-Tracking
          </h1>
          <p className="text-gray-400 mt-1">
            Automatische E-Mail-Kampagnen mit Umsatz-Attribution
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={seedDemoData} data-testid="seed-demo-btn">
            Demo-Daten
          </Button>
          <Button 
            onClick={() => setShowCreateModal(true)}
            className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
            data-testid="create-campaign-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            KI-Kampagne erstellen
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      {dashboard && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-gradient-to-br from-green-600/20 to-emerald-600/20 border-green-500/30">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-green-300">KI-Umsatz</p>
                  <p className="text-3xl font-bold text-white">{formatCurrency(dashboard.overview.total_revenue_ki)}</p>
                </div>
                <DollarSign className="w-10 h-10 text-green-400 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-blue-600/20 to-cyan-600/20 border-blue-500/30">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-blue-300">KI-Bestellungen</p>
                  <p className="text-3xl font-bold text-white">{dashboard.overview.total_orders_ki}</p>
                </div>
                <ShoppingCart className="w-10 h-10 text-blue-400 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-purple-600/20 to-pink-600/20 border-purple-500/30">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-purple-300">Reaktivierte Gäste</p>
                  <p className="text-3xl font-bold text-white">{dashboard.overview.reactivated_guests}</p>
                </div>
                <Users className="w-10 h-10 text-purple-400 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-amber-600/20 to-orange-600/20 border-amber-500/30">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-amber-300">Klickrate</p>
                  <p className="text-3xl font-bold text-white">{dashboard.email_stats.click_rate}%</p>
                </div>
                <MousePointer className="w-10 h-10 text-amber-400 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-slate-800/50 border border-slate-700 p-1">
          <TabsTrigger value="dashboard" className="data-[state=active]:bg-blue-600">
            <BarChart3 className="w-4 h-4 mr-2" />
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="triggers" className="data-[state=active]:bg-blue-600">
            <Zap className="w-4 h-4 mr-2" />
            Trigger
          </TabsTrigger>
          <TabsTrigger value="campaigns" className="data-[state=active]:bg-blue-600">
            <Mail className="w-4 h-4 mr-2" />
            Kampagnen
          </TabsTrigger>
          <TabsTrigger value="settings" className="data-[state=active]:bg-blue-600">
            <Settings className="w-4 h-4 mr-2" />
            DSGVO
          </TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-6">
          {/* KI-Insights */}
          {dashboard?.insights && dashboard.insights.length > 0 && (
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-purple-400" />
                  KI-Insights
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {dashboard.insights.map((insight, idx) => (
                    <div 
                      key={idx}
                      className="p-3 bg-slate-900/50 rounded-lg border border-slate-700"
                      data-testid={`insight-${idx}`}
                    >
                      <p className="text-lg mb-1">{insight.icon}</p>
                      <p className="text-sm text-white">{insight.message}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* E-Mail Stats */}
          {dashboard?.email_stats && (
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">E-Mail Performance</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-8">
                  <div className="text-center">
                    <p className="text-4xl font-bold text-white">{dashboard.email_stats.total_sent}</p>
                    <p className="text-sm text-gray-400">Gesendet</p>
                    <div className="mt-2 flex items-center justify-center gap-2 text-sm">
                      <Send className="w-4 h-4 text-blue-400" />
                      <span className="text-gray-400">Alle Kampagnen</span>
                    </div>
                  </div>
                  <div className="text-center">
                    <p className="text-4xl font-bold text-white">{dashboard.email_stats.open_rate}%</p>
                    <p className="text-sm text-gray-400">Öffnungsrate</p>
                    <Progress value={dashboard.email_stats.open_rate} className="mt-2 h-2" />
                  </div>
                  <div className="text-center">
                    <p className="text-4xl font-bold text-white">{dashboard.email_stats.conversion_rate}%</p>
                    <p className="text-sm text-gray-400">Konversionsrate</p>
                    <Progress value={dashboard.email_stats.conversion_rate} className="mt-2 h-2" />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Top Campaigns */}
          {dashboard?.top_campaigns && dashboard.top_campaigns.length > 0 && (
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">Top Kampagnen nach Umsatz</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {dashboard.top_campaigns.map((campaign, idx) => (
                    <div 
                      key={campaign.campaign_id || idx}
                      className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg"
                      data-testid={`top-campaign-${idx}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center text-green-400 font-bold">
                          {idx + 1}
                        </div>
                        <div>
                          <p className="font-medium text-white">{campaign.name}</p>
                          <p className="text-xs text-gray-400">{campaign.sent_count} gesendet</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-green-400">{formatCurrency(campaign.revenue_generated)}</p>
                        <p className="text-xs text-gray-400">{campaign.orders_generated} Bestellungen</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Triggers Tab */}
        <TabsContent value="triggers" className="space-y-4">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white">Automatische Trigger</CardTitle>
              <CardDescription>
                KI sendet automatisch E-Mails basierend auf diesen Auslösern
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {triggers.map((trigger, idx) => (
                  <div 
                    key={trigger.trigger_id || idx}
                    className="p-4 bg-slate-900/50 rounded-lg border border-slate-700"
                    data-testid={`trigger-${trigger.trigger_type}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl ${
                          trigger.enabled ? 'bg-blue-500/20' : 'bg-gray-500/20'
                        }`}>
                          {trigger.icon}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-medium text-white">{trigger.name}</h3>
                            {trigger.discount_percent > 0 && (
                              <Badge className="bg-green-500/20 text-green-400">
                                {trigger.discount_percent}% Rabatt
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-gray-400">{trigger.description}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            Sendezeit: {trigger.send_time} Uhr • Max. {trigger.max_emails_per_month} Mails/Monat
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right text-sm">
                          <p className="text-gray-400">{trigger.emails_sent || 0} gesendet</p>
                          <p className="text-green-400">{formatCurrency(trigger.revenue_generated || 0)}</p>
                        </div>
                        <Switch
                          checked={trigger.enabled}
                          onCheckedChange={(checked) => updateTrigger(trigger.trigger_id, { enabled: checked })}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Campaigns Tab */}
        <TabsContent value="campaigns" className="space-y-4">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-white">Kampagnen</CardTitle>
                  <CardDescription>Alle KI-generierten E-Mail-Kampagnen</CardDescription>
                </div>
                <Button onClick={() => setShowCreateModal(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Neue Kampagne
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {campaigns.length === 0 ? (
                <div className="text-center py-12">
                  <Mail className="w-16 h-16 mx-auto mb-4 text-gray-600" />
                  <h3 className="text-lg font-medium text-white mb-2">Keine Kampagnen</h3>
                  <p className="text-gray-400 mb-4">Erstellen Sie Ihre erste KI-Kampagne</p>
                  <Button onClick={() => setShowCreateModal(true)}>
                    Kampagne erstellen
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {campaigns.map((campaign, idx) => (
                    <div 
                      key={campaign.campaign_id || idx}
                      className="p-4 bg-slate-900/50 rounded-lg border border-slate-700 cursor-pointer hover:border-slate-600 transition-colors"
                      onClick={() => {
                        setSelectedCampaign(campaign);
                        setShowPreviewModal(true);
                      }}
                      data-testid={`campaign-${idx}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            campaign.status === 'sent' ? 'bg-green-500/20' :
                            campaign.status === 'draft' ? 'bg-amber-500/20' :
                            'bg-red-500/20'
                          }`}>
                            {campaign.status === 'sent' ? (
                              <CheckCircle className={`w-5 h-5 text-green-400`} />
                            ) : campaign.status === 'draft' ? (
                              <FileText className={`w-5 h-5 text-amber-400`} />
                            ) : (
                              <XCircle className={`w-5 h-5 text-red-400`} />
                            )}
                          </div>
                          <div>
                            <h3 className="font-medium text-white">{campaign.name}</h3>
                            <p className="text-sm text-gray-400">
                              {campaign.subject}
                            </p>
                            <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                              <span>{campaign.recipient_count} Empfänger</span>
                              {campaign.discount_code && (
                                <Badge variant="outline" className="text-xs">
                                  {campaign.discount_code}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-6">
                          <div className="text-right text-sm">
                            <p className="font-bold text-green-400">{formatCurrency(campaign.revenue_generated)}</p>
                            <p className="text-gray-400">{campaign.orders_generated} Bestellungen</p>
                          </div>
                          <div className="text-right text-sm">
                            <p className="text-white">{campaign.clicked_count} Klicks</p>
                            <p className="text-gray-400">{campaign.sent_count} gesendet</p>
                          </div>
                          <Badge className={
                            campaign.status === 'sent' ? 'bg-green-500' :
                            campaign.status === 'draft' ? 'bg-amber-500' :
                            'bg-red-500'
                          }>
                            {campaign.status === 'sent' ? 'Gesendet' :
                             campaign.status === 'draft' ? 'Entwurf' : 'Abgelehnt'}
                          </Badge>
                          <ChevronRight className="w-5 h-5 text-gray-500" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* DSGVO Tab */}
        <TabsContent value="settings" className="space-y-4">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white">DSGVO-Einstellungen</CardTitle>
              <CardDescription>
                Datenschutz-konforme E-Mail-Marketing Einstellungen
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {gdprSettings && (
                <>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-white">Double Opt-In erforderlich</p>
                      <p className="text-sm text-gray-400">Gäste müssen ihre E-Mail-Adresse bestätigen</p>
                    </div>
                    <Switch
                      checked={gdprSettings.settings?.require_double_optin}
                      disabled
                    />
                  </div>
                  
                  <Separator className="bg-slate-700" />
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-white">Abmelde-Link anzeigen</p>
                      <p className="text-sm text-gray-400">Jede E-Mail enthält einen Abmelde-Link</p>
                    </div>
                    <Switch
                      checked={gdprSettings.settings?.show_unsubscribe_link}
                      disabled
                    />
                  </div>
                  
                  <Separator className="bg-slate-700" />
                  
                  <div>
                    <p className="font-medium text-white mb-2">Marketing-Einwilligungstext</p>
                    <p className="text-sm text-gray-400 p-3 bg-slate-900/50 rounded-lg">
                      "{gdprSettings.settings?.marketing_consent_text}"
                    </p>
                  </div>
                  
                  <Separator className="bg-slate-700" />
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-white">Abgemeldete Gäste</p>
                      <p className="text-sm text-gray-400">Anzahl der Gäste, die sich abgemeldet haben</p>
                    </div>
                    <Badge variant="outline">{gdprSettings.optout_count || 0}</Badge>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Campaign Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-400" />
              Neue KI-Kampagne erstellen
            </DialogTitle>
            <DialogDescription>
              Die KI generiert automatisch Betreff, Text und CTA
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Kampagnen-Typ</Label>
              <Select value={selectedTriggerType} onValueChange={setSelectedTriggerType}>
                <SelectTrigger className="bg-slate-800 border-slate-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="reactivation">⏰ Reaktivierung</SelectItem>
                  <SelectItem value="welcome">👋 Willkommen</SelectItem>
                  <SelectItem value="vip_thanks">⭐ VIP-Dank</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Zielgruppe (Segment)</Label>
              <Select value={selectedSegment} onValueChange={setSelectedSegment}>
                <SelectTrigger className="bg-slate-800 border-slate-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="abwanderungsgefaehrdet">⚠️ Abwanderungsgefährdet</SelectItem>
                  <SelectItem value="vip_kunden">👑 VIP-Kunden</SelectItem>
                  <SelectItem value="neukunden">🌱 Neukunden</SelectItem>
                  <SelectItem value="stammgaeste">⭐ Stammgäste</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Rabatt (%)</Label>
              <Input
                type="number"
                value={discountPercent}
                onChange={(e) => setDiscountPercent(parseInt(e.target.value) || 0)}
                className="bg-slate-800 border-slate-700"
                min={0}
                max={50}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>
              Abbrechen
            </Button>
            <Button 
              onClick={generateCampaign}
              disabled={creatingCampaign}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {creatingCampaign ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  KI generiert...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Kampagne generieren
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Campaign Preview Modal */}
      <Dialog open={showPreviewModal} onOpenChange={setShowPreviewModal}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white">
              {selectedCampaign?.status === 'draft' ? 'Kampagne prüfen' : 'Kampagnen-Details'}
            </DialogTitle>
          </DialogHeader>
          
          {(selectedCampaign || newCampaign) && (
            <div className="space-y-4">
              {/* E-Mail Preview */}
              <div className="border border-slate-700 rounded-lg overflow-hidden">
                <div className="bg-slate-800 p-3 border-b border-slate-700">
                  <p className="text-xs text-gray-400">Betreff:</p>
                  <p className="font-medium text-white">
                    {newCampaign?.preview?.subject || selectedCampaign?.subject}
                  </p>
                </div>
                <div className="p-4 bg-white text-gray-800 min-h-[200px]">
                  <p className="whitespace-pre-line">
                    {newCampaign?.preview?.body_text || selectedCampaign?.body_text}
                  </p>
                  
                  {/* CTA Button Preview */}
                  <div className="mt-6 text-center">
                    <button className="px-6 py-3 bg-blue-600 text-white font-medium rounded-lg">
                      {newCampaign?.preview?.cta_text || selectedCampaign?.cta_text || 'Jetzt bestellen'}
                    </button>
                  </div>
                  
                  {/* Discount Code */}
                  {(newCampaign?.preview?.discount_code || selectedCampaign?.discount_code) && (
                    <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg text-center">
                      <p className="text-sm text-green-600">Dein Rabattcode:</p>
                      <p className="font-mono font-bold text-green-700 text-lg">
                        {newCampaign?.preview?.discount_code || selectedCampaign?.discount_code}
                      </p>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Campaign Info */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="p-3 bg-slate-800 rounded-lg">
                  <p className="text-gray-400">Empfänger</p>
                  <p className="text-xl font-bold text-white">
                    {newCampaign?.recipients || selectedCampaign?.recipient_count || 0}
                  </p>
                </div>
                <div className="p-3 bg-slate-800 rounded-lg">
                  <p className="text-gray-400">Rabatt</p>
                  <p className="text-xl font-bold text-green-400">
                    {newCampaign?.campaign?.discount_percent || selectedCampaign?.discount_percent || 0}%
                  </p>
                </div>
              </div>
              
              {/* Performance (for sent campaigns) */}
              {selectedCampaign?.status === 'sent' && (
                <div className="grid grid-cols-4 gap-3 text-sm">
                  <div className="p-3 bg-slate-800 rounded-lg text-center">
                    <p className="text-gray-400">Gesendet</p>
                    <p className="text-lg font-bold text-white">{selectedCampaign.sent_count}</p>
                  </div>
                  <div className="p-3 bg-slate-800 rounded-lg text-center">
                    <p className="text-gray-400">Geöffnet</p>
                    <p className="text-lg font-bold text-white">{selectedCampaign.opened_count}</p>
                  </div>
                  <div className="p-3 bg-slate-800 rounded-lg text-center">
                    <p className="text-gray-400">Geklickt</p>
                    <p className="text-lg font-bold text-white">{selectedCampaign.clicked_count}</p>
                  </div>
                  <div className="p-3 bg-green-500/20 rounded-lg text-center">
                    <p className="text-green-300">Umsatz</p>
                    <p className="text-lg font-bold text-green-400">{formatCurrency(selectedCampaign.revenue_generated)}</p>
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            {(selectedCampaign?.status === 'draft' || newCampaign) && (
              <>
                <Button 
                  variant="outline" 
                  onClick={() => rejectCampaign(newCampaign?.campaign?.campaign_id || selectedCampaign?.campaign_id)}
                  className="text-red-400 border-red-500/30 hover:bg-red-500/10"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Ablehnen
                </Button>
                <Button 
                  onClick={() => approveCampaign(newCampaign?.campaign?.campaign_id || selectedCampaign?.campaign_id)}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Freigeben & Senden
                </Button>
              </>
            )}
            {selectedCampaign?.status !== 'draft' && !newCampaign && (
              <Button variant="outline" onClick={() => setShowPreviewModal(false)}>
                Schließen
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
